import type { UserData } from "../components/auth/userData";
import { mapProductTemplateArray } from "../types/ProductTemplate";
import { mapTokenAndCompanies } from "../types/Token";
import { mapZones } from "../zones";
import { mapAvailableSubscriptions } from "./availableSubscription";
import { mapSubscriptionSearchResult } from "./subscription";
import type { SubscriptionPostContract } from "./subscriptionPostContract";
import type { SubscriptionType } from "./subscriptionType";
import { mapTicketSearchResult } from "./Ticket";

const widgetApi = {
	auth: {
		getToken: (id: string, secret: string) => ({
			method: "post" as const,
			url: "/api/auth/widget/get-token",
			body: { id, secret },
			mapper: mapTokenAndCompanies,
		}),
		verifyPhoneCode: (token: string, userData: UserData) => ({
			method: "get" as const,
			url: `/api/auth/widget/verify-phone-code?phone=${encodeURIComponent(userData.phone)}&phoneCountryCode=${encodeURIComponent(userData.phoneCountryCode)}&phoneVerificationCode=${encodeURIComponent(userData.phoneVerificationCode)}&token=${encodeURIComponent(token)}`,
		}),
	},
	zones: {
		getAll: () => ({
			method: "get" as const,
			url: "/api/zone/list",
			mapper: mapZones,
		}),
	},
	tickets: {
		getAll: (token: string, userData: UserData) => ({
			method: "post" as const,
			url: "/api/ticket/widget/get-tickets",
			body: {
				token,
				phoneCountryCode: userData.phoneCountryCode,
				phone: userData.phone,
				phoneVerificationCode: userData.phoneVerificationCode,
			},
			mapper: mapTicketSearchResult,
		}),
		cancelPending: (token: string, userData: UserData, ticketId: string) => ({
			method: "put" as const,
			url: `/api/ticket/widget/${ticketId}/cancel-pending`,
			body: {
				token,
				phoneCountryCode: userData.phoneCountryCode,
				phone: userData.phone,
				phoneVerificationCode: userData.phoneVerificationCode,
			},
		}),
		cancelActive: (
			token: string,
			userData: UserData,
			ticketId: string,
			expirationDate: Date,
		) => ({
			method: "put" as const,
			url: `/api/ticket/widget/${ticketId}/expiry/${expirationDate.toISOString()}`,
			body: {
				token,
				phoneCountryCode: userData.phoneCountryCode,
				phone: userData.phone,
				phoneVerificationCode: userData.phoneVerificationCode,
			},
		}),
		editZone: (
			token: string,
			userData: UserData,
			ticketId: string,
			timeOfChange: Date,
			zoneFrom: string,
			zoneTo: string,
		) => ({
			method: "post" as const,
			url: `/api/ticket/widget/${ticketId}/change-zone`,
			body: {
				token,
				phoneCountryCode: userData.phoneCountryCode,
				phone: userData.phone,
				phoneVerificationCode: userData.phoneVerificationCode,
				timeOfChange: timeOfChange.toISOString(),
				zoneFrom,
				zoneTo,
			},
		}),
	},
	subscriptions: {
		getAvailable: (token: string, companyId: string) => ({
			method: "post" as const,
			url: `/api/subscription/widget/available/by-company-id/${companyId}`,
			body: {
				token,
			},
			mapper: mapAvailableSubscriptions,
		}),
		create: (
			token: string,
			userData: UserData,
			agreementId: string,
			employeeId: string,
			firstName: string,
			lastName: string,
			type: SubscriptionType,
		) => {
			const body: SubscriptionPostContract = {
				token,
				phoneCountryCode: userData.phoneCountryCode,
				phone: userData.phone,
				phoneVerificationCode: userData.phoneVerificationCode,
				agreementId,
				employeeId,
				firstName,
				lastName,
				type,
			};
			return {
				method: "post" as const,
				url: "/api/subscription/widget/create",
				body,
			};
		},
		getForUser: (token: string, userData: UserData) => ({
			method: "post" as const,
			url: "/api/subscription/widget/get-subscriptions",
			body: {
				token,
				phoneCountryCode: userData.phoneCountryCode,
				phone: userData.phone,
				phoneVerificationCode: userData.phoneVerificationCode,
			},
			mapper: mapSubscriptionSearchResult,
		}),
		cancelPending: (
			token: string,
			userData: UserData,
			subscriptionId: string,
		) => ({
			method: "put" as const,
			url: `/api/subscription/widget/${subscriptionId}/cancel-pending`,
			body: {
				token,
				phoneCountryCode: userData.phoneCountryCode,
				phone: userData.phone,
				phoneVerificationCode: userData.phoneVerificationCode,
			},
		}),
		cancelActive: (
			token: string,
			userData: UserData,
			subscriptionId: string,
			expiry: Date,
		) => ({
			method: "put" as const,
			url: `/api/subscription/widget/${subscriptionId}/expiry/${expiry.toISOString()}`,
			body: {
				token,
				phoneCountryCode: userData.phoneCountryCode,
				phone: userData.phone,
				phoneVerificationCode: userData.phoneVerificationCode,
			},
		}),
	},
	products: {
		getAll: () => ({
			method: "get" as const,
			url: "/api/product/list-v2",
			mapper: mapProductTemplateArray,
		}),
	},
};

export default widgetApi;
